import { Box, Button, Grid2, Typography } from "@mui/material";
import { StepsEnum } from "./HorizontalLinearStepper";
import SNEvent from "./interfaces/SNEvent";
import RedButton from "../../Common/UI/components/RedButton";
import useFetchMsal from "../../Common/UI/hooks/useFetchMsal";
import { useState } from "react";
import { config } from "../../authConfig";
import { LoadingIndicator } from "../../Common/UI/components/Loading";
import { CancelDialog } from "./components/CancelDialog";
import useURLBuilder from "../../Common/UI/hooks/useURLBuilder";
import useIsAdminAndViewOnly from "../../Common/UI/hooks/useIsAdminAndViewOnly";
import { CommentDialog } from "./components/CommentDialog";

export default function ApprovedStep({
  event,
  setEvent,
  handleStep,
  setSteps,
  isSubmittable,
}: {
  event: SNEvent;
  handleStep: (stepNum: number) => void;
  setEvent: React.Dispatch<React.SetStateAction<SNEvent>>;
  setSteps: React.Dispatch<React.SetStateAction<string[]>>;
  isSubmittable: boolean;
}) {
  const { isViewOnly, isAdmin } = useIsAdminAndViewOnly();
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [showCommentPopup, setShowCommentPopup] = useState(false);
  const execute = useFetchMsal();
  const [isSending, setSending] = useState<boolean>(false);

  const urlBuilder = useURLBuilder();
  const handleClose = () => setShowCancelPopup(false);
  const handleCloseComments = () => setShowCommentPopup(false);
  const handleArchive = async function (comment: string) {
    try {
      setSending(true);
      setEvent((prevEvent) => {
        prevEvent.comment = comment;
        return prevEvent;
      });
      const url = `${config.rootAPIUrl}${config.rootAPIList?.archive}`;
      const response = await execute("POST", url, event);
      setEvent(response);
      setSending(false);
      handleStep(StepsEnum.New);
    } catch (error) {
      console.error(error);
      throw new Error("Event archival failed!");
    }
  };

  const handleCancel = async function (reason: string) {
    setEvent((prevEvent) => {
      event.reason = reason;
      return event;
    });
    try {
      setSending(true);
      const url = urlBuilder(`${config.rootAPIUrl}${config.rootAPIList?.cancel}`, {
        id: event.id!,
        reason: reason,
      });
      const response = await execute("POST", url);
      setEvent(response);
      setSending(false);
      setSteps(["New", "Waiting Approval", "Approved", "Cancelled", "Accounting Approval", "Invoiced", "Paid"]);
      handleStep(StepsEnum.Completed);
    } catch (error) {
      console.error(error);
      throw new Error("Event cancellation has failed!");
    }
  };

  const handleComplete = async function () {
    try {
      setSending(true);

      const url = urlBuilder(`${config.rootAPIUrl}${config.rootAPIList?.complete}`, {
        id: event.id!,
        salesContribution: event.salesContribution!,
        donationContribution: event.donationContribution!,
      });
      const response = await execute("POST", url);
      setEvent(response);
      setSending(false);
      handleStep(StepsEnum.Completed);
    } catch (error) {
      console.error(error);
      throw new Error("Event complete has failed!");
    }
  };

  return (
    <LoadingIndicator show={isSending}>
      {!isViewOnly ? (
        <Grid2 container rowSpacing={2} justifyContent={"space-between"} alignItems={"center"} sx={{ paddingBottom: "20px" }}>
          <Grid2 size={4}>
            <Box>
              <RedButton text="Complete" disabled={event.salesContribution === null || !isSubmittable} onClick={handleComplete} style={{ margin: "10px" }}></RedButton>
              <Button variant="outlined" onClick={() => setShowCancelPopup(true)} sx={{ marginLeft: 2 }}>
                Cancel
              </Button>

              {isAdmin && (
                <Button variant="outlined" onClick={() => setShowCommentPopup(true)} sx={{ marginLeft: 2 }}>
                  Archive
                </Button>
              )}

              <Typography
                sx={{
                  color: "gray",
                  fontWeight: "bold",
                  textAlign: "left",
                  marginTop: "8px",
                  marginLeft: "10px",
                }}
              >
                Verify donation total including additional donation if applicable and payable address prior to hitting complete. Once Complete, invoice is automatically generated
              </Typography>
            </Box>
          </Grid2>
          {showCommentPopup ? <CommentDialog handleCloseComments={handleCloseComments} handleAction={handleArchive} /> : <></>}

          {showCancelPopup ? <CancelDialog {...{ handleClose, handleCancel }} /> : <></>}
        </Grid2>
      ) : (
        <></>
      )}
    </LoadingIndicator>
  );
}
